import { I18n } from '@aws-amplify/core'
import { errorHasCode, errorHasMessage } from '../../../utils/errors'
import { AuthAttributes } from '../../services/Auth/Auth'
import { AppThunk } from '../../store'
import { logOutAction } from '../../rootReducer'
import { beginAuthAction, completeAuthAction, setAuthState, setAuthUser, setCognitoUser } from '.'

const errorMap = new Map<string, string>([
  [
    'CodeMismatchException',
    'Your verification code is not correct. If you have requested a new code, please check this is the most recent and try again.',
  ],
])

export const signIn =
  (username: string, password: string): AppThunk =>
  async (dispatch, _, { services }) => {
    const { authService, loggerService } = services
    try {
      dispatch(beginAuthAction())
      const { cognitoUser, challenge, challengePhoneNumber } = await authService.signIn(username, password)
      if (challenge === 'SMS_MFA') {
        dispatch(setAuthUser({ phonenumber: challengePhoneNumber }))
        dispatch(setCognitoUser(cognitoUser))
        dispatch(setAuthState('confirmSignIn'))
      } else {
        dispatch(setAuthState('authenticated'))
      }
      dispatch(completeAuthAction())
    } catch (err) {
      if (errorHasCode(err) && errorHasMessage(err)) {
        if (err.code === 'UserNotConfirmedException') {
          dispatch(setAuthState('confirmSignUp'))
          dispatch(setAuthUser({ email: username, password }))
        }
        // TODO: will this message appear on confirm account page?
        const message = I18n.get(err.message)
        dispatch(completeAuthAction({ ...err, message }))
      } else {
        dispatch(
          completeAuthAction({
            message: 'An error occured, please try again later.',
            code: 'Unknown',
          })
        )
        loggerService.logError(err)
      }
    }
  }

export const signUp =
  (attributes: AuthAttributes): AppThunk =>
  async (dispatch, _, { services }) => {
    const { authService, loggerService, analyticsService } = services
    try {
      dispatch(beginAuthAction())
      const { userSub } = await authService.signUp(attributes)
      dispatch(
        setAuthUser({
          sub: userSub,
          email: attributes.username,
          password: attributes.password,
        })
      )
      dispatch(setAuthState('confirmSignUp'))
      dispatch(completeAuthAction())
      analyticsService.createIdentity(userSub)
    } catch (err) {
      if (errorHasCode(err) && errorHasMessage(err)) {
        dispatch(completeAuthAction({ code: err.code, message: I18n.get(err.message) }))
      } else {
        dispatch(
          completeAuthAction({
            message: 'An error occured, please try again later.',
            code: 'Unknown',
          })
        )
        loggerService.logError(err)
      }
    }
  }

export const confirmSignIn =
  (code: string): AppThunk =>
  async (dispatch, getState, { services }) => {
    const { authService, loggerService } = services
    const { cognitoUser } = getState().auth
    try {
      if (!cognitoUser) {
        throw new Error('user has not signed in before confirming sign in')
      }
      dispatch(beginAuthAction())
      await authService.confirmSignIn(cognitoUser, code)
      dispatch(setAuthState('authenticated'))
      dispatch(completeAuthAction())
    } catch (err) {
      if (errorHasCode(err) && errorHasMessage(err)) {
        const mappedErrorMessage = errorMap.get(err.code)
        if (mappedErrorMessage) {
          dispatch(completeAuthAction({ code: err.code, message: mappedErrorMessage }))
        } else {
          dispatch(completeAuthAction({ code: err.code, message: I18n.get(err.message) }))
        }
      } else {
        dispatch(
          completeAuthAction({
            message: 'An error occured, please try again later.',
            code: 'Unknown',
          })
        )
        loggerService.logError(err)
      }
    }
  }

export const resendSignupCode =
  (): AppThunk =>
  async (dispatch, getState, { services }) => {
    const { authService, loggerService } = services
    const { authUser } = getState().auth
    try {
      dispatch(beginAuthAction())
      if (!authUser.email) {
        throw new Error('No email provided before resend sign up code')
      }
      await authService.resendSignUpCode(authUser.email)
      dispatch(completeAuthAction())
    } catch (err) {
      if (errorHasCode(err) && errorHasMessage(err)) {
        dispatch(completeAuthAction({ code: err.code, message: I18n.get(err.message) }))
      } else {
        dispatch(
          completeAuthAction({
            message: 'An error occured, please try again later.',
            code: 'Unknown',
          })
        )
        loggerService.logError(err)
      }
    }
  }

export const confirmSignUp =
  (code: string): AppThunk =>
  async (dispatch, getState, { services }) => {
    const { authService, loggerService } = services
    const { authUser } = getState().auth
    try {
      dispatch(beginAuthAction())
      if (!authUser.email) {
        throw new Error('No email provided before confirm sign up')
      }
      if (!authUser.password) {
        throw new Error('No email provided before confirm sign up')
      }
      await authService.confirmSignUp(authUser.email, code)
      dispatch(completeAuthAction())
      dispatch(signIn(authUser.email, authUser.password))
    } catch (err) {
      if (errorHasCode(err) && errorHasMessage(err)) {
        const mappedErrorMessage = errorMap.get(err.code)
        if (mappedErrorMessage) {
          dispatch(completeAuthAction({ code: err.code, message: mappedErrorMessage }))
        } else {
          dispatch(completeAuthAction({ code: err.code, message: I18n.get(err.message) }))
        }
      } else {
        dispatch(
          completeAuthAction({
            message: 'An error occured, please try again later.',
            code: 'Unknown',
          })
        )
        loggerService.logError(err)
      }
    }
  }

export const forgotPassword =
  (email: string): AppThunk =>
  async (dispatch, _, { services }) => {
    const { authService, loggerService } = services
    try {
      dispatch(beginAuthAction())
      await authService.forgotPassword(email)
      dispatch(setAuthUser({ email }))
      dispatch(setAuthState('forgotPassword'))
      dispatch(completeAuthAction())
    } catch (err) {
      if (errorHasCode(err) && errorHasMessage(err)) {
        dispatch(completeAuthAction({ code: err.code, message: I18n.get(err.message) }))
      } else {
        dispatch(
          completeAuthAction({
            message: 'An error occured, please try again later.',
            code: 'Unknown',
          })
        )
        loggerService.logError(err)
      }
    }
  }

export const forgotPasswordSubmit =
  (code: string, password: string): AppThunk =>
  async (dispatch, getState, { services }) => {
    const { authService, loggerService } = services
    const { email } = getState().auth.authUser
    try {
      if (!email) {
        throw new Error('No email provided before forgot password submit')
      }
      dispatch(beginAuthAction())
      await authService.forgotPasswordSubmit(email, code, password)
      dispatch(setAuthState('signIn'))
      dispatch(completeAuthAction())
    } catch (err) {
      if (errorHasCode(err) && errorHasMessage(err)) {
        dispatch(completeAuthAction({ code: err.code, message: I18n.get(err.message) }))
      } else {
        dispatch(
          completeAuthAction({
            message: 'An error occured, please try again later.',
            code: 'Unknown',
          })
        )
        loggerService.logError(err)
      }
    }
  }

export const federatedSignIn =
  (): AppThunk =>
  async (dispatch, getState, { services }) => {
    const { authService, loggerService } = services
    const federatedSignInProvider = getState().config.config.aws.aws_cognito_federated_sign_in_provider
    try {
      dispatch(beginAuthAction())
      await authService.federatedSignIn(federatedSignInProvider)
      dispatch(setAuthState('federatedSignIn')) // set auth state so loading state is shown until user is redirected to federated provider
      dispatch(completeAuthAction())
    } catch (err) {
      if (errorHasCode(err) && errorHasMessage(err)) {
        dispatch(completeAuthAction(err))
      } else {
        dispatch(
          completeAuthAction({
            message: 'An error occured, please try again later.',
            code: 'Unknown',
          })
        )
        loggerService.logError(err)
      }
    }
  }

export const goToSignUp =
  ({ email, password }: { email?: string; password?: string }): AppThunk =>
  async (dispatch) => {
    dispatch(setAuthUser({ email, password }))
    dispatch(setAuthState('signUp'))
  }

export const goToForgotPassword =
  ({ email }: { email?: string }): AppThunk =>
  async (dispatch) => {
    dispatch(setAuthUser({ email }))
    dispatch(setAuthState('forgotPassword'))
  }

export const logOut =
  (callback: () => void, { global } = { global: false }): AppThunk =>
  async (dispatch, _, { services }) => {
    const { analyticsService, authService, loggerService } = services
    try {
      dispatch(beginAuthAction())
      analyticsService.signOutUser()
      dispatch(setAuthState('signOut'))
      await authService.signOut(global)
      dispatch(completeAuthAction())
      dispatch(logOutAction())
      localStorage.clear()
      callback()
    } catch (err) {
      dispatch(setAuthState('error'))
      if (errorHasCode(err) && errorHasMessage(err)) {
        dispatch(completeAuthAction(err))
      } else {
        dispatch(
          completeAuthAction({
            message: 'An error occured, please try again later.',
            code: 'Unknown',
          })
        )
        loggerService.logError(err)
      }
    }
  }
